import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, User, Menu, X, LogOut, Package, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const navItems = [
  { to: "/boxes", label: "Curated Boxes" },
  { to: "/build-your-own", label: "Build Your Own" },
  { to: "/meat-delivery-perth", label: "Perth Delivery" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/blog", label: "Blog" },
  { to: "/wholesale", label: "Wholesale" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const ANNOUNCEMENTS = [
  { emoji: "★", text: "Free Delivery On Every Perth Metro Order" },
  { emoji: "🇦🇺", text: "WA Owned · Perth Local · Proudly Family-Run" },
  { emoji: "%", text: "Subscribe & Save 5% + Free Wagyu Beef Burgers Every Delivery" },
  // TEMPORARY (revert to "Order Thu 11pm · Pack Fri · Deliver Sat" after this week's blast)
  { emoji: "⏱", text: "Order Thu 11pm · Delivered Sat across Perth metro" },
  { emoji: "🥩", text: "6 Deliveries Unlocks Loyal Carnivore · $30 Store Credit" },
];
const ROTATE_MS = 6000;

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [annIdx, setAnnIdx] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  // Oct 2026 chrome brief: on /cart + checkout the header is compact —
  // smaller logo, cart icon, "← Back to shop", no hamburger menu.
  const compact = location.pathname === "/cart" || location.pathname.startsWith("/checkout");
  // Filter announcements per route: the Bunbury landing has its own
  // monthly cycle band + Thu-1pm cutoff — the Perth-metro cadence
  // announcement ("Order Thu 11pm · Delivered Sat") would flat-out
  // contradict it and confuse South-West shoppers. Feb 2026 fix.
  const isBunburyPage = location.pathname.startsWith("/meat-delivery-bunbury");
  const activeAnnouncements = isBunburyPage
    ? ANNOUNCEMENTS.filter((a) => !/Perth metro|Thu 11pm|Delivered Sat/i.test(a.text))
    : ANNOUNCEMENTS;

  useEffect(() => {
    const id = setInterval(() => setAnnIdx((i) => (i + 1) % activeAnnouncements.length), ROTATE_MS);
    return () => clearInterval(id);
  }, [activeAnnouncements.length]);

  const ann = activeAnnouncements[annIdx % activeAnnouncements.length];

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[color:var(--tbb-line)]" data-testid="site-header">
      {/* Announcement bar — rotating.
          Fixed min-height reserves space for the TALLEST wrapped message
          (four of the five variants wrap to 2 lines on ≤414 px viewports).
          Measured: 44 px single-line vs 49 px two-line. Using 49 px so
          rotation swaps never move the layout — Clarity was tracing 5 px
          shifts every 6 s on real users which compounded to CLS 0.4+.
          On md+ (≥ 768 px) every message fits single-line at 32 px. */}
      {!compact && <div
        className="bg-[color:var(--tbb-ink)] text-white text-[11px] tracking-[0.2em] uppercase font-bold py-2 text-center overflow-hidden min-h-[49px] md:min-h-[32px] flex items-center justify-center"
        aria-live="polite"
        aria-atomic="true"
      >
        <span
          key={annIdx}
          className="inline-flex items-center gap-2 px-4 announcement-fade"
          data-testid="rotating-announcement"
          data-idx={annIdx}
        >
          <span className="text-[color:var(--tbb-burgundy)]" aria-hidden="true">{ann.emoji}</span>
          {ann.text}
        </span>
        <style>{`
          /*
           * Fade curve for the rotating announcement bar.
           * Aug 2026 tweak — the previous 10%/90% shoulders caused the text
           * to sit at partial opacity for ~1.2s of every cycle, and screen-
           * shots captured mid-fade showed the white glyphs as dim grey on
           * the near-black background (unreadable). Compressed the fade to
           * 4%/96% so the text is at full opacity for ~5.7 of every 6.0
           * seconds — you'd have to catch the frame deliberately to see the
           * fade at all now.
           */
          @keyframes announceFade {
            0%   { opacity: 0; transform: translateY(4px); }
            4%   { opacity: 1; transform: translateY(0); }
            96%  { opacity: 1; transform: translateY(0); }
            100% { opacity: 0; transform: translateY(-4px); }
          }
          .announcement-fade {
            animation: announceFade ${ROTATE_MS}ms ease-in-out both;
          }
          @media (prefers-reduced-motion: reduce) {
            .announcement-fade { animation: none; opacity: 1; transform: none; }
          }
        `}</style>
      </div>}

      <div className={`tbb-container flex items-center justify-between ${compact ? "h-14" : "h-20"}`} data-testid={compact ? "header-compact" : "header-full"}>
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center leading-none" aria-label="THE BUTCHER BOX — home" data-testid="logo-link">
            {/*
              Header logo on every page. WebP is 6 KB vs 1 MB PNG (99.4% smaller).
              Falls back to logo.png via <picture> for legacy browsers.
              Explicit width/height stops the header from reflowing on cold loads.
            */}
            <picture>
              <source srcSet="/logo.webp" type="image/webp" />
              <img
                src="/logo.png"
                alt="THE BUTCHER BOX — Premium Meats Delivered"
                className={`${compact ? "h-9 md:h-10" : "h-14 md:h-16"} w-auto select-none`}
                width={200}
                height={64}
                decoding="async"
                fetchPriority="high"
                draggable="false"
              />
            </picture>
            <span className="sr-only" data-testid="logo-location">MALAGA · PERTH WA</span>
          </Link>
          {compact && (
            <Link to="/build-your-own" className="text-xs uppercase tracking-widest hover:underline" data-testid="header-back-to-shop">← Back to shop</Link>
          )}
          <nav className={`${compact ? "hidden" : "hidden lg:flex"} items-center gap-7`}>
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `text-[13px] font-bold uppercase tracking-[0.14em] ${
                    isActive ? "text-[color:var(--tbb-burgundy)]" : "text-[color:var(--tbb-ink)] hover:text-[color:var(--tbb-burgundy)]"
                  } transition-colors`
                }
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <button
              onClick={() => setUserMenu((v) => !v)}
              onBlur={() => setTimeout(() => setUserMenu(false), 200)}
              className="p-2 hover:bg-[color:var(--tbb-bg-soft)] transition"
              data-testid="user-menu-toggle"
              aria-label="Account"
            >
              <User size={20} />
            </button>
            {userMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-[color:var(--tbb-line)] shadow-xl py-2 z-50">
                {user ? (
                  <>
                    <div className="px-4 py-3 border-b border-[color:var(--tbb-line)]">
                      <div className="text-sm font-bold truncate">{user.name}</div>
                      <div className="text-xs text-[color:var(--tbb-muted)] truncate">{user.email}</div>
                    </div>
                    <Link to="/account" className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-[color:var(--tbb-bg-soft)]" data-testid="menu-account-link">
                      <Package size={14} /> My Account
                    </Link>
                    {user.role === "admin" && (
                      <Link to="/admin" className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-[color:var(--tbb-bg-soft)] text-[color:var(--tbb-burgundy)] font-bold" data-testid="menu-admin-link">
                        <Package size={14} /> Admin Dashboard
                      </Link>
                    )}
                    <button
                      onClick={async () => { await logout(); navigate("/"); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-[color:var(--tbb-bg-soft)] text-left"
                      data-testid="menu-logout-btn"
                    >
                      <LogOut size={14} /> Log out
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="block px-4 py-2 text-sm hover:bg-[color:var(--tbb-bg-soft)]" data-testid="menu-login-link">Log in</Link>
                    <Link to="/register" className="block px-4 py-2 text-sm hover:bg-[color:var(--tbb-bg-soft)]" data-testid="menu-register-link">Create account</Link>
                  </>
                )}
              </div>
            )}
          </div>
          <Link to="/cart" className="relative p-2 hover:bg-[color:var(--tbb-bg-soft)] transition" data-testid="cart-icon">
            <ShoppingBag size={20} />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-[color:var(--tbb-burgundy)] text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full">
                {count}
              </span>
            )}
          </Link>
          {!compact && <button
            className="lg:hidden p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>}
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[color:var(--tbb-line)] bg-white">
          <nav className="tbb-container py-4 flex flex-col gap-3">
            {navItems.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="text-sm font-bold uppercase tracking-widest py-2"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
